import { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { useSession } from '../context/SessionContext.jsx';
import { Visual } from '../components/ai/Visuals.jsx';

/**
 * Vizora AI Data Assistant.
 *
 * The browser never holds an AI token: the portal API proxies /ai/api/chat
 * and attaches the JWT server-side. So this component is a plain chat client
 * against our own origin, with the session cookie doing the authenticating.
 *
 * The client owns the conversation history — the backend is stateless per
 * request — so history lives in component state and is sent whole each time.
 */
export default function Assistant() {
  const { user } = useSession();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [lastFailed, setLastFailed] = useState(null);
  /* Demo visitors get a small allowance. Held here rather than read from
     status on every render because status is cached for five minutes, and a
     counter that lags the thing it counts is worse than none. */
  const [demo, setDemo] = useState(null);

  const contentRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const { data: status, isLoading } = useQuery({
    queryKey: ['ai', 'status'], queryFn: () => api.get('/ai/status'), retry: false
  });

  useEffect(() => { if (status?.demo) setDemo(status.demo); }, [status]);

  // Keep the newest turn in view as answers arrive.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, pending]);

  const send = useCallback(async (text, historyOverride) => {
    const history = historyOverride ?? [...messages, { role: 'user', content: text }];
    if (!historyOverride) setMessages(history);
    setDraft('');
    setError(null);
    setLastFailed(null);
    setPending(true);

    try {
      // Re-measure per send: the assistant shapes its output to the space
      // available (narrow -> charts rather than wide tables).
      const box = contentRef.current?.getBoundingClientRect();
      const result = await api.post('/ai/api/chat', {
        messages: history.map(m => ({ role: m.role, content: m.content })),
        viewport: {
          width: Math.max(320, Math.round(box?.width ?? 900)),
          height: Math.max(320, Math.round(box?.height ?? 700))
        }
      });

      setMessages([...history, {
        role: 'assistant',
        content: result.reply ?? '',
        visuals: Array.isArray(result.visuals) ? result.visuals : [],
        toolsUsed: Array.isArray(result.toolsUsed) ? result.toolsUsed : []
      }]);
      // The server is the authority on what is left; it decrements only when
      // it actually answered.
      if (result.demo) setDemo(result.demo);
    } catch (err) {
      // Never auto-retry: every attempt costs metered model tokens. Offer a
      // manual retry instead and keep the question so it isn't retyped.
      setError(err.body?.error ?? err.message ?? 'Something went wrong');
      /* Not a failure to retry - offering "Try again" would invite them to
         press it and be refused again. But only a spent personal allowance
         closes the composer: somebody stopped by the daily ceiling still has
         their own questions and should find them there tomorrow, and telling
         them otherwise makes a working product look broken. */
      const code = err.body?.details?.code;
      const spent = code === 'email_allowance_spent';
      const refused = spent || code === 'daily_limit_reached' || code === 'demo_request_too_large';
      if (spent) {
        setDemo(d => ({ ...(d ?? {}), remaining: 0, available: false }));
      } else if (typeof err.body?.details?.remaining === 'number') {
        setDemo(d => ({ ...(d ?? {}), remaining: err.body.details.remaining }));
      }
      setLastFailed(refused ? null : history);
    } finally {
      setPending(false);
      inputRef.current?.focus();
    }
  }, [messages]);

  const submit = (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || pending) return;
    send(text);
  };

  const newConversation = () => {
    setMessages([]); setDraft(''); setError(null); setLastFailed(null);
    inputRef.current?.focus();
  };

  if (isLoading) {
    return <div className="embed-overlay"><div className="spinner-border text-primary" /></div>;
  }

  if (!status?.enabled) {
    // Admins get the specific blockers and how to clear each one; everyone
    // else gets a plain message, since the deployment's configuration state
    // is not their business.
    const blockers = status?.blockers ?? [];
    return (
      <div className="ai-shell">
        <div className="ai-unavailable">
          <i className="bi bi-stars" />
          <h1 className="h5 mt-3">Vizora AI Data Assistant</h1>

          {!user?.isAdmin || blockers.length === 0 ? (
            /* The server's own words. It knows which of the three switches is
               off; this page guessing "contact Vizora" sent people to the
               wrong place whenever the answer was a tick box their own
               administrator owns. */
            <p className="text-secondary mb-0">
              {status?.message
                ?? 'The assistant is not enabled for your account. '
                   + 'Your portal administrator can turn it on.'}
            </p>
          ) : (
            <>
              <p className="text-secondary">
                Not live yet. {blockers.length === 1
                  ? 'One thing is outstanding:'
                  : `${blockers.length} things are outstanding:`}
              </p>
              <ol className="ai-blockers">
                {blockers.map(b => (
                  <li key={b.code}>
                    <div className="fw-semibold">{b.summary}</div>
                    <code className="ai-blocker-fix">{b.fix}</code>
                  </li>
                ))}
              </ol>
              <p className="text-secondary small mb-0">
                Only administrators see this detail.
                {status?.tenant && <> Tenant slug: <code>{status.tenant}</code>.</>}
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="ai-shell" ref={contentRef}>
      <header className="ai-header">
        <div className="d-flex align-items-center gap-2 min-w-0">
          <span className="ai-mark"><i className="bi bi-stars" /></span>
          <div className="min-w-0">
            <div className="ai-title">Vizora AI Data Assistant</div>
            <div className="ai-subtitle text-truncate">
              Ask about your data{status?.profile ? ` · ${status.profile}` : ''}
              {demo && (
                <span className="ms-2">
                  · {demo.remaining} of {demo.total} demo question
                  {demo.total === 1 ? '' : 's'} left
                </span>
              )}
            </div>
          </div>
        </div>
        <button type="button" className="btn btn-sm btn-outline-secondary"
                onClick={newConversation} disabled={pending || messages.length === 0}>
          <i className="bi bi-arrow-clockwise" /> New conversation
        </button>
      </header>

      {/* The monthly cap, above the conversation rather than only as a failed
          send. Someone arriving at an already-capped portal should see why
          before typing a question, not after waiting for it to be refused.

          Deliberately not a full-screen blocker like the availability panel
          above: the history is still worth reading, and this clears by itself
          on the 1st. Status is cached for five minutes, so a user who was fine
          on load can still be refused mid-session — the error bubble covers
          that case with the same wording. */}
      {status?.spendCap && (status.spendCap.blocked || status.spendCap.level === 'warning') && (
        <div className={`alert ${status.spendCap.blocked ? 'alert-danger' : 'alert-warning'} py-2 small rounded-0 mb-0`}>
          <i className={`bi ${status.spendCap.blocked ? 'bi-pause-circle' : 'bi-exclamation-triangle'} me-1`} />
          {status.spendCap.blocked
            ? status.spendCap.message
            : 'Your organisation is close to its monthly limit for the assistant.'}
          {/* Figures only for administrators; the server omits them for
              everyone else, so there is nothing here to hide client-side. */}
          {status.spendCap.summary && (
            <span className="ms-1 text-secondary">{status.spendCap.summary}</span>
          )}
        </div>
      )}

      <div className="ai-scroll" ref={scrollRef}>
        {messages.length === 0 && !pending && (
          <Welcome name={user?.fullName} onPick={(q) => send(q)} />
        )}

        {messages.map((m, i) => (
          <Turn key={i} message={m} />
        ))}

        {pending && (
          <div className="ai-turn ai-turn-assistant">
            <div className="ai-avatar"><i className="bi bi-stars" /></div>
            <div className="ai-bubble ai-thinking">
              <span className="ai-dot" /><span className="ai-dot" /><span className="ai-dot" />
              <span className="ms-2 small text-secondary">Working through your data…</span>
            </div>
          </div>
        )}

        {error && (
          <div className="ai-turn ai-turn-assistant">
            <div className="ai-avatar ai-avatar-error"><i className="bi bi-exclamation-triangle" /></div>
            <div className="ai-bubble ai-bubble-error">
              <div>{error}</div>
              {lastFailed && (
                <button type="button" className="btn btn-sm btn-outline-danger mt-2"
                        onClick={() => send(null, lastFailed)} disabled={pending}>
                  Try again
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {demo && demo.available === false ? (
        <div className="ai-composer">
          <div className="w-100 text-center py-2">
            <div className="fw-medium mb-1">That is the end of your demo questions</div>
            <div className="small text-secondary mb-2">
              Book time with the Vizora team to see the assistant answering on your own data.
            </div>
            <a className="btn btn-sm btn-primary" href="https://vizora.co.uk/contact">
              Book time with the team
            </a>
          </div>
        </div>
      ) : (
      <form className="ai-composer" onSubmit={submit}>
        <textarea
          ref={inputRef}
          className="form-control"
          rows={1}
          value={draft}
          disabled={pending}
          placeholder="Ask about your data — for example, top 5 customers by revenue"
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(e); }
          }}
        />
        <button type="submit" className="btn btn-primary"
                disabled={pending || !draft.trim()}
                aria-label="Send">
          {pending
            ? <span className="spinner-border spinner-border-sm" />
            : <i className="bi bi-send-fill" />}
        </button>
      </form>
      )}

      {/* Outside the composer ternary on purpose: it belongs under a spent
          demo allowance as much as under the input box, and a caveat that
          disappears in some states is worse than none. */}
      <p className="ai-disclaimer">
        Vizora AI Assistant is AI and can make mistakes. Please double-check all responses.
      </p>
    </div>
  );
}

/* ------------------------------- a turn -------------------------------- */

function Turn({ message }) {
  if (message.role === 'user') {
    return (
      <div className="ai-turn ai-turn-user">
        <div className="ai-bubble ai-bubble-user">{message.content}</div>
      </div>
    );
  }

  return (
    <div className="ai-turn ai-turn-assistant">
      <div className="ai-avatar"><i className="bi bi-stars" /></div>
      <div className="ai-bubble">
        {/* Visuals first, in order, then the reply as the comment on them. */}
        {message.visuals?.map((v, i) => <Visual key={i} visual={v} />)}
        {message.content && <ReplyText text={message.content} />}
        {message.toolsUsed?.length > 0 && (
          <div className="ai-tools" title="Answered using governed queries against your warehouse">
            <i className="bi bi-shield-check" /> answered via {message.toolsUsed.length} governed{' '}
            {message.toolsUsed.length === 1 ? 'query' : 'queries'}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The reply is plain text that may contain simple markdown. Rendered as text
 * with line breaks and **bold** only — never as HTML, so nothing the model
 * emits can inject markup.
 */
function ReplyText({ text }) {
  return (
    <div className="ai-reply">
      {String(text).split('\n').map((line, i) => (
        <p key={i} className={line.trim() ? undefined : 'ai-reply-gap'}>
          {line.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
            part.startsWith('**') && part.endsWith('**')
              ? <strong key={j}>{part.slice(2, -2)}</strong>
              : <span key={j}>{part}</span>
          )}
        </p>
      ))}
    </div>
  );
}

/* ------------------------------ welcome -------------------------------- */

const SUGGESTIONS = [
  'Top 5 customers by revenue',
  'Which invoices are overdue?',
  'Show revenue by month as a trend',
  'How is the sales pipeline looking?'
];

function Welcome({ name, onPick }) {
  return (
    <div className="ai-welcome">
      <span className="ai-mark ai-mark-lg"><i className="bi bi-stars" /></span>
      <h2 className="h5 mt-3 mb-1">
        Hello{name ? `, ${name.split(' ')[0]}` : ''}
      </h2>
      <p className="text-secondary">
        Ask a question about your data and I’ll answer from your own warehouse.
      </p>
      <div className="ai-suggestions">
        {SUGGESTIONS.map(s => (
          <button key={s} type="button" className="ai-suggestion" onClick={() => onPick(s)}>
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}
